import { t } from "i18next";
import React, { Fragment, useEffect, useState } from "react";
import { Controller, useForm, useFormContext, useWatch } from "react-hook-form";
import { Trans } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";

import { AtiraDatePicker } from "../AtiraDatePicker";
import { formatDateTimeWithoutTimezone } from "../../utils/Date";
import { AtiraSelect } from "../AtiraSelect";
import { Drawer } from "../Drawer";
import { Flex } from "../Flex";
import { Input } from "../Input";
import { Text } from "../Text";
import { Textarea } from "../Textarea";
import { TaskPriority } from "../../model/entry/types/TaskPriority.enum";
import { Lengths } from "../../model/shared/enum/Lengths.enum";
import { UserRoles } from "../../model/user/types/UserRoles";
import { footerActions } from "../../redux/footer-daily-stats/footer-daily-stats.slice";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { AtiraToast } from "../../utils/AtiraToast";
import { EntityAssignCheckbox } from "../EntityAssignCheckbox";
import { Radio, Rate } from "../../utils/local-antd";
import { SettingsSubRoutes } from "../../model/app/types/SettingsSubRoutes.enum";
import { taskActions } from "../../redux/task/task.slice";
import { TaskKind } from "../../model/task/shared/TasksKind.enum";
import { CreateTaskDto } from "../../model/task/dto/CreateTaskDto";
import { DrawerDefaultsExtra } from "../../routes/tasks/components/DrawerDefaultsExtra";
import { getTaskPriorityColor } from "../../utils/taskUtils";
import { useDrawersContext } from "../../routes/tasks/drawers-context";
import fetchAfterTaskAction from "./functions/fetchAfterTaskAction";
import { taskSliceSelectors } from "../../redux/task/task.selector";
import { appSliceSelectors } from "../../redux/app/app.selector";
import { GetTasksFilterDto } from "../../model/task/dto/GetTasksFilterDto";
import { TestIDs } from "../../tests/types/testIds";
import { ImageUpload } from "../ImageUpload";

const StyledInput = styled(Input)`
  height: 2.5rem;
  border: 2px solid ${({ theme }) => theme.main};
`;

const StyledRadio = styled(Radio.Button)<{ priority: TaskPriority }>`
  border: ${({ theme, priority }) =>
    `1px solid ${theme[getTaskPriorityColor(priority)]}`} !important;
  color: ${({ theme, priority }) =>
    theme[getTaskPriorityColor(priority)]} !important;

  &.ant-radio-button-wrapper-checked {
    background-color: ${({ theme, priority }) =>
      theme[getTaskPriorityColor(priority)]} !important;
    color: ${({ theme }) => theme.white} !important;
  }
  &.ant-radio-button-wrapper::before {
    display: none;
  }
`;

const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.lightMain};
  text-decoration: underline;
  text-underline-offset: 5px;

  &:hover {
    color: ${({ theme }) => theme.main};
  }
`;

const validateTaskName = (value: string) => {
  if (!value.trim() || value.trim().length < 3) {
    return t("task.drawer.error.short_name");
  }
};

export const TaskCreateDrawer: React.FC = () => {
  const [taskCreateLoading, setTaskCreateLoading] = useState(false);

  const {
    taskKind,
    setTaskKind,
    entry,
    kanbanCard,
    taskCreateVisible,
    setTaskCreateVisible,
    setEntry,
    setTask,
    setKanbanCard,
  } = useDrawersContext();
  const { getValues: contextGetValues } = useFormContext<GetTasksFilterDto>();
  const { pathname } = useLocation();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const tasksPage = useAppSelector(taskSliceSelectors.selectTasksPage);
  const tasksPageSize = useAppSelector(taskSliceSelectors.selectTasksPageSize);
  const searchOptions = useAppSelector(appSliceSelectors.selectSearchOptions);

  const isOrganization = user?.roles?.includes(UserRoles.ORGANIZATION);

  const dispatch = useAppDispatch();

  const { control, getValues, reset, handleSubmit } = useForm<CreateTaskDto>();

  const [watchedReminder, watchedDeadline] = useWatch({
    control,
    name: ["reminder", "deadline"],
  });

  const createTask = async () => {
    try {
      setTaskCreateLoading(true);

      const dto = getValues();

      if (dto?.reminder) {
        dto.reminder = formatDateTimeWithoutTimezone(dto.reminder);
      }

      if (dto?.deadline) {
        dto.deadline = formatDateTimeWithoutTimezone(dto.deadline);
      }

      await dispatch(
        taskActions.createTask({ ...dto, kind: taskKind! }),
      ).unwrap();
      await fetchAfterTaskAction({
        userId,
        entryId: entry?._id!,
        kanbanCardId: kanbanCard?._id,
        pathname,
        tasksPage,
        tasksPageSize,
        dispatch,
        setEntry,
        setTask,
        setKanbanCard,
        contextGetValues,
        searchKeyword: searchOptions?.keyword,
      });

      dispatch(footerActions.getFooterDailyStats({ userId }));
      localOnClsoe();
      AtiraToast.success(t("task.create.success"));
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setTaskCreateLoading(false);
    }
  };

  const localOnClsoe = () => {
    setTaskCreateVisible(false);
    setTaskKind(null);
    reset({
      parentId: "",
      userId: userId,
      ownerId: userId,
      priority: TaskPriority.MEDIUM,
      description: "",
      done: undefined,
      name: "",
      reminder: undefined,
      deadline: undefined,
      assigneeIds: [],
      kind: undefined,
    });
  };

  useEffect(() => {
    if (taskCreateVisible) {
      reset({
        parentId: pathname.includes("/deals") ? kanbanCard?._id : entry?._id,
        userId: userId,
        ownerId: userId,
        priority: TaskPriority.MEDIUM,
        description: "",
        done: undefined,
        name: "",
        reminder: undefined,
        deadline: undefined,
        assigneeIds: [],
        kind: taskKind || undefined,
      });
    }
  }, [
    entry?._id,
    kanbanCard?._id,
    pathname,
    taskCreateVisible,
    reset,
    userId,
    taskKind,
  ]);

  if (!taskKind) {
    return null;
  }

  return (
    <Drawer
      title={t("common.task_create")}
      open={taskCreateVisible}
      onClose={localOnClsoe}
      extra={
        <DrawerDefaultsExtra
          onSubmit={handleSubmit(createTask)}
          onCancel={localOnClsoe}
          submitTestId={TestIDs.tasks.createEdit.drawer.button}
          config={{ loading: taskCreateLoading }}
        />
      }
      maskClosable={false}
      size="large"
    >
      <Flex flexDirection="column" gap="m">
        {taskKind !== TaskKind.ORPHAN ? (
          <AtiraSelect
            title={t("task.create.leads_dropdown.title")}
            disabled
            options={[
              {
                label: entry?.fields?.name || entry?.fields?.email,
                value: entry?._id,
              },
            ]}
            value={{
              // eslint-disable-next-line no-nested-ternary
              label: [TaskKind.CONTACT, TaskKind.LEAD].includes(taskKind)
                ? entry?.fields?.name || entry?.fields?.email
                : taskKind === TaskKind.KANBAN
                  ? kanbanCard?.entry?.fields?.name ||
                    kanbanCard?.entry?.fields?.email ||
                    kanbanCard?.title
                  : "",
              // eslint-disable-next-line no-nested-ternary
              value: [TaskKind.CONTACT, TaskKind.LEAD].includes(taskKind)
                ? entry?._id
                : taskKind === TaskKind.KANBAN
                  ? kanbanCard?._id
                  : "",
            }}
            onChange={() => {}}
            style={{ width: "100%" }}
            className={TestIDs.tasks.createEdit.drawer.selectContact}
          />
        ) : null}

        <Controller
          control={control}
          name="name"
          rules={{
            required: {
              value: true,
              message: t("common.this_field_required"),
            },
            validate: (value: string) => validateTaskName(value),
            maxLength: {
              value: Lengths.DESCRIPTION,
              message: t("error.length.less", {
                name: t("common.task_name"),
                length: Lengths.DESCRIPTION,
              }),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <StyledInput
              value={value}
              onChange={onChange}
              title={t("common.task_name")}
              required
              valid={!error}
              errorMessage={error?.message}
              maxLength={Lengths.DESCRIPTION}
              dataTestId={TestIDs.tasks.createEdit.drawer.name}
            />
          )}
        />

        <Controller
          control={control}
          name="reminder"
          render={({ field: { value, onChange } }) => (
            <AtiraDatePicker
              onChange={onChange}
              value={value || null}
              title={t("common.remind_date")}
              showTime
              info={
                <Trans
                  i18nKey={"common.dateStoredByTimezone.note"}
                  components={[
                    <StyledLink
                      to={`/settings/${SettingsSubRoutes.NOTIFICATIONS}`}
                    >
                      {t("common.settings")}
                    </StyledLink>,
                  ]}
                />
              }
              bottomNote={t("common.sendReminder.note")}
              fromToday
              maxDateValue={watchedDeadline}
              dataTestId={TestIDs.tasks.createEdit.drawer.reminder}
            />
          )}
        />

        <Controller
          control={control}
          name="deadline"
          render={({ field: { value, onChange } }) => (
            <AtiraDatePicker
              value={value || null}
              onChange={(date) => onChange(date)}
              title={t("common.deadline")}
              showTime
              fromToday
              minDateValue={watchedReminder}
              dataTestId={TestIDs.tasks.createEdit.drawer.deadline}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          render={({ field: { value, onChange } }) => (
            <Textarea
              rows={3}
              title={t("common.description")}
              value={value}
              onChange={onChange}
              maxLength={Lengths.LONG}
              dataTestId={TestIDs.tasks.createEdit.drawer.description}
            />
          )}
        />

        {taskKind === TaskKind.CONTACT ? (
          <Fragment>
            <Flex flexDirection="column" gap="s">
              <Text>{t("task.update.image.title")}</Text>
              <Controller
                control={control}
                name="image"
                render={({ field: { value, onChange } }) => (
                  <ImageUpload
                    image={value || ""}
                    onChange={(f) => onChange(f)}
                  />
                )}
              />
            </Flex>

            <Flex flexDirection="column" gap="s">
              <Text>{t("task.update.rating.title")}</Text>
              <Controller
                control={control}
                name="rating"
                render={({ field: { value, onChange } }) => (
                  <Rate
                    count={10}
                    value={value}
                    onChange={onChange}
                    className={TestIDs.tasks.createEdit.drawer.rating}
                  />
                )}
              />
            </Flex>
          </Fragment>
        ) : null}

        {isOrganization ? (
          <Controller
            control={control}
            name="assigneeIds"
            render={({ field: { value, onChange } }) => (
              <EntityAssignCheckbox value={value || []} onChange={onChange} />
            )}
          />
        ) : null}

        {[TaskKind.CONTACT, TaskKind.ORPHAN].includes(taskKind) ? (
          <Flex flexDirection="column" gap="s">
            <Text>{t("common.priority")}</Text>
            <Controller
              control={control}
              name="priority"
              render={({ field: { value, onChange } }) => (
                <Radio.Group onChange={onChange} value={value}>
                  {Object.values(TaskPriority).map((op) => (
                    <StyledRadio
                      key={op}
                      value={op}
                      priority={op}
                      className={TestIDs.tasks.createEdit.drawer.priority(op)}
                    >
                      {t(`common.${op.toLowerCase()}`)}
                    </StyledRadio>
                  ))}
                </Radio.Group>
              )}
            />
          </Flex>
        ) : null}
      </Flex>
    </Drawer>
  );
};
